import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { BRAND } from "../../data/content";
import { useCart } from "../../context/CartContext";
import { CART } from "../../constants/testIds/shop";
import { LOGOUT } from "../../constants/testIds/auth";
import { useAuth } from "../../context/AuthContext";
import { useCategoryNav } from "../../hooks/useCategoryNav";

const PRIMARY = [
  { label: "Shop All", to: "/shop", testid: "nav-link-shop" },
  { label: "New Arrivals", to: "/#new-arrivals", testid: "nav-link-new-arrivals" },
  { label: "Our Story", to: "/#our-story", testid: "nav-link-our-story" },
];

const navItemCls =
  "group relative inline-flex items-center gap-1 whitespace-nowrap py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-brand-charcoal/80 transition-colors duration-200 hover:text-brand-gold after:absolute after:-bottom-0.5 after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-brand-gold after:transition-transform after:duration-300 hover:after:scale-x-100";

// ---------------------------------------------------------------- Mega-menu
const CollectionsMega = ({ categories, onNavigate, onMouseEnter, onMouseLeave }) => (
  <motion.div
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 8 }}
    transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
    className="fixed inset-x-0 top-16 z-[60] px-4 sm:px-6 lg:top-[72px] lg:px-8"
    data-testid="nav-collections-mega-menu"
  >
    <div
      style={{
        maxWidth: "980px",
        margin: "0 auto",
        background: "#ffffff",
        border: "1px solid rgba(176,148,96,0.22)",
        boxShadow: "0 40px 80px -40px rgba(28,25,23,0.5)",
        display: "grid",
        gridTemplateColumns: "1.5fr 1fr",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "2rem" }}>
        <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-brand-gold">
          <span style={{ height: 1, width: 24, background: "rgba(176,148,96,0.6)" }} /> Shop by Style
        </p>
        <div style={{ marginTop: "1.4rem", display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: "2.5rem" }}>
          {categories.length
            ? categories.slice(0, 8).map((c) => (
                <Link
                  key={c.id}
                  to={`/shop?category=${c.id}`}
                  onClick={onNavigate}
                  data-testid={`collection-mega-link-${c.id}`}
                  className="group"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid rgba(231,226,216,0.7)",
                    padding: "0.65rem 0",
                  }}
                >
                  <span
                    className="font-serif text-brand-charcoal transition-colors group-hover:text-brand-gold"
                    style={{ fontSize: "1.05rem" }}
                  >
                    {c.label}
                  </span>
                  <span style={{ fontSize: 11, color: "rgba(120,113,108,0.7)" }}>{c.count}</span>
                </Link>
              ))
            : Array.from({ length: 6 }).map((_, i) => (
                <span
                  key={i}
                  className="animate-pulse"
                  style={{ height: 40, borderBottom: "1px solid rgba(231,226,216,0.5)" }}
                />
              ))}
        </div>
      </div>
      <Link
        to="/shop"
        onClick={onNavigate}
        aria-label="Shop all bracelets"
        className="group"
        style={{
          position: "relative",
          minHeight: 264,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          overflow: "hidden",
        }}
      >
        <img
          src="/brand/heena-square.jpg"
          alt="HESHAURA handcrafted bracelet edit"
          className="transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          style={{ position: "absolute", inset: 0, height: "100%", width: "100%", objectFit: "cover" }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(28,25,23,0.82), rgba(28,25,23,0.15), transparent)",
          }}
        />
        <div style={{ position: "relative", padding: "1.5rem" }}>
          <p className="font-serif" style={{ fontSize: "1.5rem", fontStyle: "italic", color: "rgb(250,247,242)" }}>
            The Bracelet Edit
          </p>
          <p
            style={{
              marginTop: 8,
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.22em",
              color: "rgb(232,216,181)",
            }}
          >
            Shop the collection →
          </p>
        </div>
      </Link>
    </div>
  </motion.div>
);

// ---------------------------------------------------------------- Mobile menu
const MobileMenu = ({ open, onClose, user, logout, categories }) => {
  const [collOpen, setCollOpen] = useState(false);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex flex-col bg-brand-cream lg:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex items-center justify-between border-b border-brand-sand px-5 py-4">
            <span className="font-serif text-2xl font-light tracking-[0.28em] text-brand-charcoal">{BRAND.name}</span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close menu"
              data-testid="mobile-menu-close-button"
              className="flex h-11 w-11 items-center justify-center text-brand-charcoal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-6 py-8">
            <Link
              to="/shop"
              onClick={onClose}
              data-testid="nav-link-shop"
              className="block py-3 font-serif text-3xl font-medium text-brand-charcoal transition-colors hover:text-brand-gold"
            >
              Shop All
            </Link>
            <button
              type="button"
              onClick={() => setCollOpen((v) => !v)}
              data-testid="mobile-collections-toggle"
              className="flex w-full items-center justify-between py-3 font-serif text-3xl font-medium text-brand-charcoal"
            >
              Collections
              <ChevronDown className={`h-6 w-6 text-brand-gold transition-transform ${collOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence initial={false}>
              {collOpen && (
                <motion.ul
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden pl-1"
                >
                  {categories.slice(0, 10).map((c) => (
                    <li key={c.id}>
                      <Link
                        to={`/shop?category=${c.id}`}
                        onClick={onClose}
                        data-testid={`mobile-collection-link-${c.id}`}
                        className="flex items-center justify-between border-b border-brand-sand/60 py-2.5 text-base text-brand-charcoal/90 transition-colors hover:text-brand-gold"
                      >
                        {c.label}
                        <span className="text-xs tabular-nums text-brand-stone/60">{c.count}</span>
                      </Link>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
            <Link
              to="/#new-arrivals"
              onClick={onClose}
              data-testid="nav-link-new-arrivals"
              className="block py-3 font-serif text-3xl font-medium text-brand-charcoal transition-colors hover:text-brand-gold"
            >
              New Arrivals
            </Link>
            <Link
              to="/#our-story"
              onClick={onClose}
              data-testid="nav-link-our-story"
              className="block py-3 font-serif text-3xl font-medium text-brand-charcoal transition-colors hover:text-brand-gold"
            >
              Our Story
            </Link>
            <div className="gold-rule my-8" />
            {user ? (
              <>
                <Link
                  to="/account"
                  onClick={onClose}
                  data-testid="mobile-account-link"
                  className="block text-xs font-semibold uppercase tracking-[0.2em] text-brand-gold"
                >
                  My Account
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    logout();
                    onClose();
                  }}
                  className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-stone"
                >
                  Logout · {(user.name || "there").split(" ")[0]}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={onClose}
                className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-gold"
              >
                Sign in / Create account
              </Link>
            )}
          </nav>
          <div className="border-t border-brand-sand px-6 py-5">
            <p className="font-serif text-lg italic text-brand-stone">{BRAND.tagline}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ---------------------------------------------------------------- Header
export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const closeTimer = useRef();
  const openMega = () => {
    clearTimeout(closeTimer.current);
    setMegaOpen(true);
  };
  const closeMega = () => {
    closeTimer.current = setTimeout(() => setMegaOpen(false), 150);
  };
  const { itemCount, openCart } = useCart();
  const { user, logout } = useAuth();
  const categories = useCategoryNav();

  const iconBtn =
    "flex h-11 w-11 items-center justify-center text-brand-charcoal transition-colors duration-200 hover:text-brand-gold";

  return (
    <>
      <header
        className="sticky top-0 z-40 border-b border-brand-sand/70 bg-brand-cream/85 backdrop-blur-xl"
        data-testid="site-header"
      >
        <div className="mx-auto grid h-16 max-w-7xl grid-cols-[1fr_auto_1fr] items-center px-4 sm:px-6 lg:h-[72px] lg:px-8">
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-button"
              className={`${iconBtn} -ml-2 lg:hidden`}
            >
              <Menu className="h-5 w-5" />
            </button>
            <nav className="hidden items-center gap-8 lg:flex" data-testid="header-navigation">
              <Link to="/shop" data-testid="nav-link-shop" className={navItemCls}>
                Shop All
              </Link>
              <div className="relative" onMouseEnter={openMega} onMouseLeave={closeMega}>
                <button
                  type="button"
                  data-testid="nav-link-collections"
                  className={navItemCls}
                  onClick={() => setMegaOpen((v) => !v)}
                  onFocus={openMega}
                  aria-expanded={megaOpen}
                  aria-haspopup="true"
                >
                  Collections
                  <ChevronDown className={`h-3 w-3 text-brand-gold/80 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {megaOpen && (
                    <CollectionsMega
                      categories={categories}
                      onNavigate={() => setMegaOpen(false)}
                      onMouseEnter={openMega}
                      onMouseLeave={closeMega}
                    />
                  )}
                </AnimatePresence>
              </div>
              <Link to="/#new-arrivals" data-testid="nav-link-new-arrivals" className={navItemCls}>
                New Arrivals
              </Link>
              <Link to="/#our-story" data-testid="nav-link-our-story" className={navItemCls}>
                Our Story
              </Link>
            </nav>
          </div>

          <Link to="/" data-testid="header-logo" className="group flex items-center justify-center" aria-label="HESHAURA home">
            <img
              src="/brand/heshaura-logo.png"
              alt="HESHAURA"
              className="h-[34px] w-auto max-w-full object-contain transition-opacity duration-300 group-hover:opacity-80 md:h-[40px] lg:h-[44px]"
            />
          </Link>

          <div className="flex items-center justify-end gap-0.5 sm:gap-1.5">
            {user ? (
              <span className="hidden items-center gap-3 sm:flex" data-testid="header-account-state">
                <Link
                  to="/account"
                  className="text-xs font-medium text-brand-charcoal transition-colors hover:text-brand-gold"
                  data-testid="header-account-name"
                >
                  Hi, {(user.name || "there").split(" ")[0]}
                </Link>
                <button
                  type="button"
                  onClick={logout}
                  data-testid={LOGOUT.button}
                  className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-stone transition-colors hover:text-brand-gold"
                >
                  Logout
                </button>
              </span>
            ) : (
              <Link to="/login" aria-label="Account" data-testid="header-account-link" className={iconBtn}>
                <User className="h-[18px] w-[18px]" strokeWidth={1.6} />
              </Link>
            )}
            <Link to="/shop?focus=search" aria-label="Search bracelets" data-testid="search-trigger-button" className={iconBtn}>
              <Search className="h-[18px] w-[18px]" strokeWidth={1.6} />
            </Link>
            <button
              type="button"
              aria-label="Shopping bag"
              data-testid="bag-button"
              onClick={openCart}
              className={`${iconBtn} relative -mr-2`}
            >
              <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.6} />
              {itemCount > 0 && (
                <span
                  data-testid={CART.count}
                  className="absolute right-0.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-brand-gold text-[9px] font-bold text-brand-cream"
                >
                  {itemCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} user={user} logout={logout} categories={categories} />
    </>
  );
};
